import { ReactNode } from "react";
import { RyeMainContentPanel } from "./RyeMainContentPanel";
import {
  RyeMainContentContainerBase,
  STANDARD_MAIN_CONTENT_CARD_PADDING_X,
} from "./RyeMainContentContainerBase";
import { RyeMainContentHeaderRow } from "./RyeMainContentHeader";
import { buildClasses } from "../utils/buildClasses";
import { RYE_PANEL_SURFACE } from "./RyeScreenWrapper";
import { RyeIcon } from "./RyeIcon";

/**
 * Secondary navigation: a panel of its own, beside the content's panel, or a
 * tinted strip inside it when `embedded`. It shares the content's top-bar
 * height.
 */
export function RyeMainContentSidebar({
  title,
  joinHeader = false,
  wide = false,
  embedded = false,
  children,
}: {
  title: string;
  /**
   * Draw the title and the navigation as one surface, without the spacer
   * between them. The title still lines up with the content's top bar.
   */
  joinHeader?: boolean;
  /** Room for longer navigation labels, such as "Category breakdown". */
  wide?: boolean;
  /**
   * Draw as a tinted strip along the left of the content's panel, divided from
   * it by a hairline, like System Settings' sidebar. For modals, where panels
   * floating inside the frame look busy.
   */
  embedded?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        { if: wide, then: "w-[14.75rem]", else: "w-[11.75rem]" },
        "shrink-0",
        "h-full",
        "min-h-0",
        {
          if: embedded,
          then: buildClasses(
            "bg-surface-300",
            "border-r",
            "border-edge/[0.08]",
            // Follows the panel's rounded corners, inside its 1px border.
            "rounded-l-[calc(0.8rem-1px)]",
          ),
          else: buildClasses("relative", RYE_PANEL_SURFACE),
        },
      )}
    >
      {joinHeader ? (
        <RyeMainContentContainerBase
          padTop={true}
          fixedHeader={
            <div className={STANDARD_MAIN_CONTENT_CARD_PADDING_X}>
              <RyeMainContentHeaderRow title={title} />
            </div>
          }
        >
          {children}
        </RyeMainContentContainerBase>
      ) : (
        <RyeMainContentPanel title={title}>{children}</RyeMainContentPanel>
      )}
    </div>
  );
}

/** Classes for an item in a sidebar's navigation, such as a link or button. */
export function ryeSidebarNavItemClasses(isSelected: boolean): string {
  return buildClasses(
    "flex",
    "items-center",
    "gap-3",
    "p-2",
    "rounded-md",
    "cursor-pointer",
    "select-none",
    {
      if: isSelected,
      then: buildClasses("text-purple", "bg-on-surface-200"),
      else: buildClasses("text-on-surface-500", "hover:text-on-surface-800"),
    },
  );
}

/** The icon and label of an item in a sidebar's navigation. */
export function RyeSidebarNavItemContent({
  icon,
  label,
  isSelected,
}: {
  icon: string;
  label: string;
  isSelected: boolean;
}): JSX.Element {
  return (
    <>
      <RyeIcon
        name={icon}
        fill={isSelected}
        size="custom"
        className={buildClasses("text-[1.2rem]")}
      />
      <span className={buildClasses("text-sm", "whitespace-nowrap")}>
        {label}
      </span>
    </>
  );
}
